import { collection, setDoc, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { financialRecordToFirestore, financialRecordFromFirestore } from "../models/financialRecord";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";
import { formatCurrency } from "../utils/format";
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from "../constants/financial";

/**
 * FinancialRecordService
 * Centraliza as operações de Contas a Pagar e Receber do NeoApp.
 * 
 * Coleção: tenants/{idTenant}/financialRecords
 * 
 * Cada documento é um lançamento financeiro autocontido (flat),
 * cobrindo tanto INCOME (receita/receber) quanto EXPENSE (despesa/pagar).
 */
const FinancialRecordService = {

    // =====================================================================
    // LEITURA (Queries otimizadas para o Firebase)
    // =====================================================================

    /**
     * Busca registros financeiros por período de VENCIMENTO.
     * Query principal do Fluxo de Caixa.
     * 
     * @param {string} idTenant 
     * @param {Date} startDate - Início do período
     * @param {Date} endDate - Fim do período
     * @param {string} [type] - FINANCIAL_TYPES.INCOME ou FINANCIAL_TYPES.EXPENSE (opcional, sem ele traz ambos)
     * @returns {Promise<Array>} Lista de registros
     */
    getByDueDate: async (idTenant, startDate, endDate, type = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            // dueDate é string "YYYY-MM-DD" — comparaçã lexicográfica funciona corretamente
            const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
            const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];
            const constraints = [
                where("dueDate", ">=", startStr),
                where("dueDate", "<=", endStr),
                orderBy("dueDate", "asc")
            ];

            if (type) {
                constraints.unshift(where("type", "==", type));
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                ...constraints
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => financialRecordFromFirestore(doc));
        } catch (error) {
            console.error("Erro ao buscar registros por vencimento:", error);
            throw error;
        }
    },

    /**
     * Busca registros por COMPETÊNCIA (para DRE / Relatório Gerencial).
     */
    getByCompetenceDate: async (idTenant, startDate, endDate, type = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            // competenceDate é string "YYYY-MM-DD"
            const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
            const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];
            const constraints = [
                where("competenceDate", ">=", startStr),
                where("competenceDate", "<=", endStr),
                orderBy("competenceDate", "asc")
            ];

            if (type) {
                constraints.unshift(where("type", "==", type));
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                ...constraints
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => financialRecordFromFirestore(doc));
        } catch (error) {
            console.error("Erro ao buscar registros por competência:", error);
            throw error;
        }
    },

    /**
     * Busca registros por STATUS (ex: listar todos OVERDUE / PENDING).
     */
    getByStatus: async (idTenant, status, type = null, maxResults = 100) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const constraints = [
                where("status", "==", status),
                orderBy("dueDate", "asc"),
                limit(maxResults)
            ];

            if (type) {
                constraints.unshift(where("type", "==", type));
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                ...constraints
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => financialRecordFromFirestore(doc));
        } catch (error) {
            console.error("Erro ao buscar registros por status:", error);
            throw error;
        }
    },

    /**
     * Busca todos os registros financeiros de um membro específico.
     * Útil para a aba financeira no perfil do aluno.
     */
    getByMember: async (idTenant, idMember) => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do Membro são obrigatórios.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                where("idMember", "==", idMember),
                orderBy("dueDate", "desc")
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => financialRecordFromFirestore(doc));
        } catch (error) {
            console.error("Erro ao buscar registros do membro:", error);
            throw error;
        }
    },

    /**
     * Busca todos os registros financeiros vinculados a uma venda.
     */
    getBySale: async (idTenant, idSale) => {
        if (!idTenant || !idSale) throw new Error("ID do Tenant e da Venda são obrigatórios.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                where("idSale", "==", idSale),
                orderBy("currentInstallment", "asc")
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => financialRecordFromFirestore(doc));
        } catch (error) {
            console.error("Erro ao buscar registros da venda:", error);
            throw error;
        }
    },

    /**
     * Busca um único registro pelo ID.
     */
    getById: async (idTenant, recordId) => {
        if (!idTenant || !recordId) throw new Error("ID do Tenant e do Registro são obrigatórios.");
        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS, recordId);
            const snapshot = await getDoc(docRef);
            return financialRecordFromFirestore(snapshot);
        } catch (error) {
            console.error("Erro ao buscar registro financeiro:", error);
            throw error;
        }
    },

    // =====================================================================
    // ESCRITA (CRUD com Auditoria)
    // =====================================================================

    /**
     * Cria ou atualiza um registro financeiro.
     */
    save: async (formData, idTenant, authUser, existingRecords = [], batch = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const recordData = financialRecordToFirestore(formData, { id: idTenant }, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const recordRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS, formData.id);
                const oldRecord = existingRecords.find(r => r.id === formData.id) || {};
                if (batch) {
                    batch.update(recordRef, recordData);
                } else {
                    await updateDoc(recordRef, recordData);
                }

                // Auditoria
                const changes = AuditService.calculateDiff(oldRecord, recordData);
                if (changes) {
                    AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.FINANCIAL_RECORDS,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: formData.id,
                        entityName: `${recordData.type} - ${recordData.entityName} - ${formatCurrency(recordData.grossAmount)}`,
                        description: `Atualizou lançamento financeiro de "${recordData.entityName}"`,
                        details: changes
                    });
                }

                return { id: formData.id, ...recordData };
            } else {
                // CREATE
                const newDocRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS));

                if (batch) {
                    batch.set(newDocRef, recordData);
                } else {
                    await setDoc(newDocRef, recordData);
                }

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.FINANCIAL_RECORDS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: `${recordData.type} - ${recordData.entityName} - ${formatCurrency(recordData.grossAmount)}`,
                    description: `Criou lançamento ${recordData.type === FINANCIAL_TYPES.ASSET ? 'a receber' : 'a pagar'} para "${recordData.entityName}"`
                });

                return { id: newDocRef.id, ...recordData };
            }
        } catch (error) {
            console.error("Erro no FinancialRecordService.save:", error);
            throw error;
        }
    },

    /**
     * Marca um registro como PAGO.
     * Atalho rápido para a ação mais comum do gestor.
     * 
     * @param {string} recordId - ID do registro
     * @param {Object} paymentInfo - { paymentDate, idBankAccount, bankAccountName, paymentMethod, amountPaid, observation, documentNumber }
     */
    markAsPaid: async (recordId, idTenant, authUser, paymentInfo = {}) => {
        if (!idTenant || !recordId) throw new Error("ID do Tenant e do Registro são obrigatórios.");

        try {
            const recordRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS, recordId);

            const updateData = {
                status: FINANCIAL_STATUS.PAID,
                paymentDate: paymentInfo.paymentDate || new Date().toISOString().split('T')[0], // string "YYYY-MM-DD"
                idBankAccount: paymentInfo.idBankAccount || null,
                bankAccountName: paymentInfo.bankAccountName || "",
                documentNumber: paymentInfo.documentNumber || "",
                updatedAt: Timestamp.now(), // auditoria → Timestamp
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            // Campos opcionais do modal de baixa
            if (paymentInfo.paymentMethod) updateData.paymentMethod = paymentInfo.paymentMethod;
            if (paymentInfo.amountPaid) updateData.amountPaid = Number(paymentInfo.amountPaid);
            if (paymentInfo.observation) updateData.observation = paymentInfo.observation;

            await updateDoc(recordRef, updateData);

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.FINANCIAL_RECORDS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: recordId,
                entityName: `Baixa de pagamento #${recordId.substring(0, 8)}`,
                description: `Marcou registro #${recordId.substring(0, 8)} como PAGO${paymentInfo.bankAccountName ? ` via ${paymentInfo.bankAccountName}` : ""}`,
                details: {
                    status: FINANCIAL_STATUS.PAID,
                    banco: paymentInfo.bankAccountName || "-",
                    metodo: paymentInfo.paymentMethod || "-",
                    valorPago: paymentInfo.amountPaid || "-",
                    dataPagamento: paymentInfo.paymentDate || "-"
                }
            });

            return { id: recordId, ...updateData };
        } catch (error) {
            console.error("Erro ao marcar como pago:", error);
            throw error;
        }
    },

    /**
     * Cancela um registro financeiro.
     */
    cancel: async (recordId, idTenant, authUser, reason = "") => {
        if (!idTenant || !recordId) throw new Error("ID do Tenant e do Registro são obrigatórios.");

        try {
            const recordRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS, recordId);

            const updateData = {
                status: FINANCIAL_STATUS.CANCELED,
                cancellationDate: new Date().toISOString().split('T')[0], // data de calendário → string "YYYY-MM-DD"
                cancellationReason: reason || "Cancelado pelo gestor",
                updatedAt: Timestamp.now(), // auditoria → Timestamp
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            await updateDoc(recordRef, updateData);

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.FINANCIAL_RECORDS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: recordId,
                entityName: `Cancelamento #${recordId.substring(0, 8)}`,
                description: `Cancelou registro #${recordId.substring(0, 8)}. Motivo: ${reason || "Não informado"}`,
                details: { status: FINANCIAL_STATUS.CANCELED, motivo: reason || "-" }
            });

            return { id: recordId, ...updateData };
        } catch (error) {
            console.error("Erro ao cancelar registro:", error);
            throw error;
        }
    },

    /**
     * Remove um registro financeiro (hard delete).
     * Usar com cautela — preferir cancel() para manter histórico.
     */
    delete: async (recordId, idTenant, authUser, recordDescription = "") => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS, recordId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.FINANCIAL_RECORDS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: recordId,
                entityName: recordDescription,
                description: `Excluiu permanentemente o registro "${recordDescription || recordId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no FinancialRecordService.delete:", error);
            throw error;
        }
    }
};

export default FinancialRecordService;
